import React, {useCallback, useMemo, useState} from 'react'
import {isIOS} from 'react-device-detect'
import QRCode from 'react-qr-code'
import Skeleton from 'react-loading-skeleton'
import styled from 'styled-components'
import {
  AUTH_LOGIN_MAX_ATTEMPT,
  AUTHENTICATOR_GOOGLE_APP_STORE_URL,
  AUTHENTICATOR_GOOGLE_PLAY_STORE_URL,
  AUTHENTICATOR_MICROSOFT_APP_STORE_URL,
  AUTHENTICATOR_MICROSOFT_PLAY_STORE_URL,
  AUTHENTICATOR_OTP_LENGTH,
  WINDOW_MODE_TABLET_WIDTH,
} from 'consts'
import {useTranslation} from 'i18n'
import {
  copyToClipboard,
  formatCooldownTime,
  formatCooldownTimeToString,
  getFontFamily,
  getTextShadow,
  showSnackbar,
  textSpacer,
  useLocation,
} from 'utils'
import {useTheme} from 'themes'
import {ObjectState, parseShape, SettingAuthenticatorScreenType} from 'types'
import {useWindowMode} from 'windows'
import {requestData} from 'services'
import {Button, Icon, InputOTP, Paragraph, ParsedText} from 'common/components'
import convertUnit from 'lib/unit'
import {useDispatch} from 'lib/redux'
import GiftShopEditProfileLimitPasswordModal from 'pages/gift-shop/edit-profile/containers/Modal/GiftShopEditProfileLimitPasswordModal'

const StyledContainer = styled.div`
  overflow-y: scroll;
  scrollbar-width: none;
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-self: center;
  background-color: ${({theme}) => theme.white_3};
`
const StyledContentContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  border-radius: ${convertUnit(16)};
  text-align: center;
  max-width: ${convertUnit(560)};
  margin-top: ${convertUnit(20)};
  padding: 0 ${convertUnit(20)};
  height: fit-content;
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    max-width: 100%;
    height: 100%;
    border-radius: 0;
    margin-top: 0;
  }
  background-color: ${({theme}) => theme.white_1};
`
const StyledBodyContainer = styled.div`
  overflow-y: scroll;
  scrollbar-width: none;
  padding-bottom: ${convertUnit(20)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    max-width: 100%;
    height: 100%;
    border-radius: 0;
  }
`
const StyledTitle = styled(Paragraph)`
  margin: ${convertUnit(16)} 0 ${convertUnit(12)} 0;
`
const StyledTutorial = styled.div`
  display: flex;
  flex-direction: row;
  text-align: left;
  gap: ${convertUnit(8)};
`
const StyledQrContainer = styled.div`
  background-color: ${({theme}) => theme.white_3};
  display: flex;
  flex-direction: column;
  padding: ${convertUnit(20)} ${convertUnit(16)};
  border-radius: ${convertUnit(8)};
  border: ${convertUnit(1)} solid ${({theme}) => theme.white_5};
  margin-top: ${convertUnit(20)};
`
const StyledQrImageContainer = styled.div`
  padding: ${convertUnit(8)};
  background-color: ${({theme}) => theme.white_1};
  display: flex;
  justify-content: center;
  width: fit-content;
  align-self: center;
  margin-top: ${convertUnit(20)};
  box-shadow: ${({theme}) =>
    getTextShadow(theme, {blurRadius: 8, opacity: 0.14})};
`
const StyledKeyContainer = styled.div`
  background-color: ${({theme}) => theme.white_2};
  display: flex;
  flex-direction: column;
  padding: ${convertUnit(8)};
  border-radius: ${convertUnit(8)};
  border: ${convertUnit(1)} solid ${({theme}) => theme.gray_1};
`
const StyledCopyKeyText = styled(Paragraph)`
  cursor: pointer;
`
const StyledInputContainer = styled.div`
  margin: ${convertUnit(20)} 0;
`

export interface GiftShopSettingAuthenticatorProps {
  stateScreen: ObjectState<SettingAuthenticatorScreenType>
}
export interface GiftShopSettingAuthenticatorSetupVerifProps
  extends GiftShopSettingAuthenticatorProps {
  stateSecurityAnswer: ObjectState<string>
}

export default function GiftShopSettingAuthenticatorSetup({
  stateScreen,
  stateSecurityAnswer,
}: GiftShopSettingAuthenticatorSetupVerifProps) {
  const {translate} = useTranslation()
  const theme = useTheme()
  const mode = useWindowMode()
  const {update} = useDispatch()
  const {uri} = useLocation('giftshop_setting_authenticator').state
  const setScreen = stateScreen[1]
  const [codes, setCodes] = useState('')
  const [answer, setAnswer] = stateSecurityAnswer
  const stateError = useState('')
  const [error, setError] = stateError
  const [loading, setLoading] = useState(false)
  const [cooldownTimer, setCooldownTimer] = useState('0s')
  const [limitModal, setLimitModal] = useState(false)

  const parseText = useMemo<parseShape[]>(
    () => [
      {
        pattern: /<b>.*?<\/b>/,
        style: {fontFamily: getFontFamily('medium')},
        renderText: (text) => text.replace(/<b>|<\/b>/g, ''),
      },
      {
        pattern: /<google>.*?<\/google>/,
        style: {
          fontFamily: getFontFamily('medium'),
          color: theme.primary_5,
          textDecorationLine: 'underline',
          cursor: 'pointer',
        },
        onClick: () =>
          window.open(
            isIOS
              ? AUTHENTICATOR_GOOGLE_APP_STORE_URL
              : AUTHENTICATOR_GOOGLE_PLAY_STORE_URL,
            '_blank',
          ),
        renderText: (text) => text.replace(/<google>|<\/google>/g, ''),
      },
      {
        pattern: /<ms>.*?<\/ms>/,
        style: {
          fontFamily: getFontFamily('medium'),
          color: theme.primary_5,
          textDecorationLine: 'underline',
          cursor: 'pointer',
        },
        onClick: () =>
          window.open(
            isIOS
              ? AUTHENTICATOR_MICROSOFT_APP_STORE_URL
              : AUTHENTICATOR_MICROSOFT_PLAY_STORE_URL,
            '_blank',
          ),
        renderText: (text) => text.replace(/<ms>|<\/ms>/g, ''),
      },
    ],
    [theme.primary_5],
  )

  const handleCheckCode = useCallback(() => {
    setLoading(true)
    requestData('mfa_activate', {
      data: {code: codes, security_answer: answer},
      onRequestReceived: () => setLoading(false),
      onRequestSuccess: ({status, data: {detail}}) => {
        if (status === 200) {
          update('settingState', {isAuthenticatorEnabled: true})
          setAnswer('')
          setScreen('success')
        } else if (status === 400 && detail && detail.mfa_attempt) {
          setError(
            translate('giftShop:settingAuthAppInvalidError', {
              count: AUTH_LOGIN_MAX_ATTEMPT - parseInt(detail.mfa_attempt, 10),
            }),
          )
        } else if (status === 423 && detail && detail.suspend_duration) {
          update('settingState', {
            mfa_suspend_time: new Date().getTime(),
            mfa_suspend_duration: formatCooldownTime(detail.suspend_duration),
          })
          setCooldownTimer(
            formatCooldownTimeToString(detail.suspend_duration, false, true),
          )
          setLimitModal(true)
        } else {
          setError(
            translate('global:messageError', {context: `code-${status}`}),
          )
        }
      },
    })
  }, [answer, codes, setAnswer, setError, setScreen, translate, update])

  const handleRenderInput = useMemo(
    () => (
      <StyledQrContainer>
        <Paragraph fontSize="l" fontWeight="bold">
          {translate('giftShop:settingAuthAppInputDesc', {
            length: AUTHENTICATOR_OTP_LENGTH,
          })}
        </Paragraph>
        <StyledInputContainer>
          <InputOTP
            boxes={AUTHENTICATOR_OTP_LENGTH}
            blurOnRender
            onSubmit={(code) => setCodes(code)}
            containerStyle={{
              height: mode === 'mobile' ? 40 : 50,
              width: mode === 'mobile' ? 40 : 50,
            }}
            stateError={stateError}
          />
          {error && (
            <Paragraph fontSize="m" fontWeight="medium" color="danger_5">
              {error}
            </Paragraph>
          )}
        </StyledInputContainer>
        <Button
          label={translate('global:verify')}
          disabled={codes.length < AUTHENTICATOR_OTP_LENGTH}
          isLoading={loading}
          onClick={handleCheckCode}
        />
      </StyledQrContainer>
    ),
    [
      codes.length,
      error,
      handleCheckCode,
      loading,
      mode,
      stateError,
      translate,
    ],
  )

  const handleRenderQr = useMemo(
    () =>
      uri && (
        <>
          <StyledQrContainer>
            {Array.from({length: 3}).map((_, index) => (
              <StyledTutorial key={`${index + 1}`}>
                <Paragraph fontSize="m">{index + 1}.</Paragraph>
                <Paragraph fontSize="m">
                  <ParsedText parse={parseText}>
                    {translate('giftShop:settingAuthAppTutorial', {
                      context: `${index + 1}`,
                    })}
                  </ParsedText>
                </Paragraph>
              </StyledTutorial>
            ))}
            <StyledQrImageContainer>
              {uri ? (
                <QRCode value={uri} size={150} />
              ) : (
                <Skeleton
                  width={150}
                  height={150}
                  baseColor={theme.gray_2}
                  highlightColor={theme.white_1}
                />
              )}
            </StyledQrImageContainer>
            <StyledTitle fontSize="m">
              {translate('giftShop:settingAuthAppEnterKey')}
            </StyledTitle>
            <StyledKeyContainer>
              <Paragraph fontSize="m" fontWeight="medium">
                {textSpacer(uri.split('&secret=')[1])}
              </Paragraph>
              <StyledCopyKeyText
                fontSize="m"
                fontWeight="medium"
                color="primary_5"
                onClick={() =>
                  copyToClipboard(uri.split('&secret=')[1], () => {
                    showSnackbar(translate('giftShop:settingAuthAppCopied'))
                  })
                }>
                <Icon type="copy" color="primary_5" />
                {translate('giftShop:settingAuthAppCopyKey')}
              </StyledCopyKeyText>
            </StyledKeyContainer>
          </StyledQrContainer>
        </>
      ),
    [parseText, theme.gray_2, theme.white_1, translate, uri],
  )

  const handleRenderSetupScreen = useMemo(
    () => (
      <StyledContainer>
        <StyledContentContainer>
          <StyledBodyContainer>
            {handleRenderQr}
            {handleRenderInput}
          </StyledBodyContainer>
        </StyledContentContainer>
      </StyledContainer>
    ),
    [handleRenderInput, handleRenderQr],
  )

  const handleRenderModalLimit = useMemo(
    () =>
      limitModal && (
        <GiftShopEditProfileLimitPasswordModal
          cooldownTimer={cooldownTimer}
          onConfirm={() => {
            setLimitModal(false)
            history.back()
          }}
          title={translate('giftShop:attemptLimitExceeded')}
        />
      ),
    [cooldownTimer, limitModal, translate],
  )

  return (
    <>
      {handleRenderSetupScreen}
      {handleRenderModalLimit}
    </>
  )
}
